import React, { useEffect, useMemo, useState } from "react";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { useTrialBalanceQuery } from "@api/accountingReports.api";
import { DownloadDropdown } from "@components/accounting/balanceSheet/DownloadDropdown";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { GlDrilldownDrawer } from "@components/accounting/generalLedger/GlDrilldownDrawer";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { trialBalanceFilterDefinition } from "../accountingReportFilterDefinitions";
import { AccountingReportViewToolbar } from "../reportViews/AccountingReportViewToolbar";
import { useAccountingTabContext } from "../useAccountingTab";
import { useGlDrilldownDrawerState } from "../useGlDrilldownDrawerState";
import { TrialBalancePageContent } from "./TrialBalancePageContent";
import { useTrialBalanceDownload } from "./useTrialBalanceDownload";

export const TrialBalancePage: React.FC = () => {
    const { filters, setFilters, selectedEntity, ledgerBasis } =
        useAccountingTabContext();
    const {
        openDrillDown,
        closeDrillDown,
        isOpen: isGlDrawerOpen,
        drawerKey,
        drawerTarget,
    } = useGlDrilldownDrawerState();
    const filtersWithDefaultValues = useMemo(() => {
        if (filters.cycle === undefined) {
            return {
                ...filters,
                cycle: trialBalanceFilterDefinition.defaultCycle,
            };
        }
        return filters;
    }, [filters]);

    const [generationDate, setGenerationDate] = useState(() => new Date());

    const trialBalance = useTrialBalanceQuery(filtersWithDefaultValues);

    useEffect(() => {
        if (trialBalance.data) {
            setGenerationDate(new Date());
        }
    }, [trialBalance.data, setGenerationDate]);

    const { isDownloading, download } = useTrialBalanceDownload(
        filtersWithDefaultValues,
        trialBalance.data?.presentation?.rowVisibility,
    );

    const downloadButton = (
        <DownloadDropdown
            onDownload={(format) =>
                selectedEntity &&
                download({
                    entity: selectedEntity,
                    format,
                    date: filtersWithDefaultValues.endDate,
                })
            }
            loading={isDownloading}
            disabled={trialBalance.isLoading || !trialBalance.isSuccess}
        />
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_TRIAL_BALANCE_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    return (
        <>
            <AccountingReportViewToolbar
                reportId="trialBalance"
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
            >
                {downloadButton}
            </AccountingReportViewToolbar>
            <AccountingReportFiltersForm
                {...trialBalanceFilterDefinition.filterProps}
                showFinancialAccountFilter
                filters={filtersWithDefaultValues}
                setFilters={setFilters}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                shouldTrackDatePreset
                hideSearch
                showCyclesSelect
            />
            {selectedEntity && (
                <LoaderOnInitialLoading
                    queryResult={trialBalance}
                    dimWhileFetching
                >
                    {trialBalance.data && selectedEntity && (
                        <TrialBalancePageContent
                            data={trialBalance.data}
                            filters={filtersWithDefaultValues}
                            chrome={trialBalance.data.presentation.chrome}
                            numberFormat={
                                trialBalance.data.presentation.numberFormat
                            }
                            generationDate={generationDate}
                            onDrillDown={openDrillDown}
                        />
                    )}
                </LoaderOnInitialLoading>
            )}
            {drawerTarget && (
                <GlDrilldownDrawer
                    key={drawerKey}
                    open={isGlDrawerOpen}
                    onClose={closeDrillDown}
                    filters={drawerTarget.filters}
                    entityIds={drawerTarget.entityIds}
                    ledgerBasis={ledgerBasis}
                    classifications={drawerTarget.classifications}
                    pnlBalanceSince={drawerTarget.pnlBalanceSince}
                />
            )}
        </>
    );
};
